import { useState } from 'react';
import { toast } from 'react-toastify';
import { Lock, Shield, Eye, EyeOff, Smartphone, Key, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@components/ui';

export default function SecuritySettings() {
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const [passwords, setPasswords] = useState({
        current: '',
        new: '',
        confirm: '',
    });

    const [twoFA, setTwoFA] = useState(false);

    const toggleShowPassword = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handlePasswordChange = (field, value) => {
        setPasswords(prev => ({ ...prev, [field]: value }));
    };

    const handleUpdatePassword = () => {
        if (passwords.new !== passwords.confirm) {
            toast.error('Şifreler eşleşmiyor');
            return;
        }
        if (passwords.new.length < 8) {
            toast.error('Şifre en az 8 karakter olmalıdır');
            return;
        }
        toast.success('Şifreniz başarıyla güncellendi');
        setPasswords({ current: '', new: '', confirm: '' });
    };

    // Password Strength Indicator
    const getPasswordStrength = (password) => {
        if (!password) return { level: 0, text: '', color: '' };
        if (password.length < 6) return { level: 1, text: 'Zayıf', color: 'bg-error' };
        if (password.length < 10) return { level: 2, text: 'Orta', color: 'bg-warning' };
        if (/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(password)) {
            return { level: 3, text: 'Güçlü', color: 'bg-success' };
        }
        return { level: 2, text: 'Orta', color: 'bg-warning' };
    };

    const strength = getPasswordStrength(passwords.new);

    const PasswordInput = ({ label, field, placeholder }) => (
        <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{label}</label>
            <div className="relative">
                <input
                    type={showPasswords[field] ? 'text' : 'password'}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-border-light bg-bg-primary text-text-primary placeholder:text-text-muted focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder={placeholder}
                    value={passwords[field]}
                    onChange={(e) => handlePasswordChange(field, e.target.value)}
                />
                <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                    onClick={() => toggleShowPassword(field)}
                >
                    {showPasswords[field] ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-8">
            <div>
                <h2 className="text-xl font-bold text-text-primary">Güvenlik Ayarları</h2>
                <p className="text-text-tertiary text-sm mt-1">Hesap güvenliğinizi yönetin</p>
            </div>

            {/* Password Change Section */}
            <div className="bg-bg-tertiary/30 rounded-xl border border-border-light p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Key size={20} className="text-primary" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-text-primary">Şifre Değiştir</h3>
                        <p className="text-sm text-text-tertiary">Güvenlik için şifrenizi düzenli olarak değiştirin</p>
                    </div>
                </div>

                <div className="space-y-4 max-w-md">
                    <PasswordInput label="Mevcut Şifre" field="current" placeholder="••••••••" />

                    <div>
                        <PasswordInput label="Yeni Şifre" field="new" placeholder="En az 8 karakter" />
                        {passwords.new && (
                            <div className="mt-2">
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                                        <div
                                            className={`h-full ${strength.color} transition-all`}
                                            style={{ width: `${strength.level * 33.33}%` }}
                                        />
                                    </div>
                                    <span className={`text-xs font-medium ${strength.level === 3 ? 'text-success' :
                                            strength.level === 2 ? 'text-warning' : 'text-error'
                                        }`}>
                                        {strength.text}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <PasswordInput label="Yeni Şifre (Tekrar)" field="confirm" placeholder="Şifreyi tekrar girin" />

                    {passwords.new && passwords.confirm && (
                        <div className={`flex items-center gap-2 text-sm ${passwords.new === passwords.confirm ? 'text-success' : 'text-error'
                            }`}>
                            {passwords.new === passwords.confirm ? (
                                <>
                                    <CheckCircle size={16} />
                                    <span>Şifreler eşleşiyor</span>
                                </>
                            ) : (
                                <>
                                    <AlertTriangle size={16} />
                                    <span>Şifreler eşleşmiyor</span>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="mt-6 pt-6 border-t border-border-light">
                    <Button variant="primary" icon={<Lock size={18} />} onClick={handleUpdatePassword}>
                        Şifreyi Güncelle
                    </Button>
                </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="bg-bg-tertiary/30 rounded-xl border border-border-light p-6">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center shrink-0">
                            <Smartphone size={20} className="text-warning" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-text-primary">İki Faktörlü Doğrulama (2FA)</h3>
                            <p className="text-sm text-text-tertiary mt-1">
                                Hesabınıza ekstra güvenlik katmanı ekleyin. Giriş yaparken telefonunuza gelen kodu girmeniz gerekecek.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setTwoFA(!twoFA);
                            toast.success(twoFA ? '2FA devre dışı bırakıldı' : '2FA etkinleştirildi');
                        }}
                        className={`relative w-14 h-7 rounded-full transition-all duration-200 shrink-0 ${twoFA ? 'bg-success' : 'bg-neutral-300'
                            }`}
                    >
                        <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform duration-200 ${twoFA ? 'translate-x-7' : 'translate-x-0'
                            }`} />
                    </button>
                </div>

                {twoFA && (
                    <div className="mt-4 p-4 rounded-xl bg-success/5 border border-success/20 flex items-center gap-3">
                        <Shield size={20} className="text-success" />
                        <span className="text-sm text-success font-medium">İki faktörlü doğrulama aktif</span>
                    </div>
                )}
            </div>

            {/* Active Sessions */}
            <div className="bg-bg-tertiary/30 rounded-xl border border-border-light p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-tertiary/10 flex items-center justify-center">
                        <Shield size={20} className="text-tertiary" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-text-primary">Aktif Oturumlar</h3>
                        <p className="text-sm text-text-tertiary">Hesabınıza bağlı cihazları yönetin</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-bg-secondary border border-primary/20">
                        <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-success" />
                            <div>
                                <p className="text-sm font-medium text-text-primary">Bu Cihaz</p>
                                <p className="text-xs text-text-tertiary">Chrome • İstanbul, Türkiye</p>
                            </div>
                        </div>
                        <span className="text-xs text-success font-medium">Aktif</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
